import React, { useMemo } from 'react';
import { RefreshCw, UserPlus } from 'lucide-react';
import clsx from 'clsx';
import { StatusIcon } from '@components/common';
import type { ImportRow, RowStatus, RowAction } from '@app-types/import';
import type { TableColumn } from '@app-types';
import { RZTable } from '@components/common/table';
import TierBadge from '@components/common/TierBadge';
import { Select } from '@components/common';
import { tierToVariant } from '@utils/badgeMappers';
import styles from './ImportPreviewTable.module.css';

type FilterType = 'all' | 'valid' | 'warning' | 'error' | 'conflict';

/**
 * Pull the quoted value out of a backend conflict message
 * (e.g. `Contract Owner email not found: "a@b.com"` -> `a@b.com`), falling back
 * to the whole message when it isn't quoted. Uses indexOf rather than a regex:
 * the equivalent `/^.*"(.+)"$/` is vulnerable to catastrophic backtracking.
 */
const extractQuotedValue = (message: string): string => {
  const close = message.lastIndexOf('"');
  if (close <= 0) return message;
  const open = message.lastIndexOf('"', close - 1);
  return open === -1 ? message : message.slice(open + 1, close);
};

const IMPORT_PREVIEW_COLUMNS: TableColumn[] = [
  { key: 'rowNumber', label: 'Row', width: '50px', centered: true, sortable: false, defaultVisible: true },
  { key: 'status', label: 'Status', width: '60px', centered: true, sortable: false, defaultVisible: true },
  { key: 'action', label: 'Action', width: '100px', centered: true, sortable: false, defaultVisible: true },
  { key: 'contract_number', label: 'Contract #', width: '120px', sortable: false, defaultVisible: true },
  { key: 'supplier', label: 'Supplier', width: '180px', sortable: false, defaultVisible: true },
  { key: 'tier', label: 'Tier', width: '100px', sortable: false, defaultVisible: true },
  { key: 'details', label: 'Details', sortable: false, defaultVisible: true }
];

interface ImportPreviewTableProps {
  rows: ImportRow[];
  filter: FilterType;
  maxRows?: number;
  rowOverrides?: Record<number, RowAction>;
  onActionChange?: (rowNumber: number, action: RowAction) => void;
}

const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({
  rows,
  filter,
  maxRows = 100,
  rowOverrides = {},
  onActionChange
}) => {
  const filteredRows = useMemo(() => {
    let filtered = rows;

    if (filter !== 'all') {
      filtered = rows.filter((row) => {
        // When filtering by 'error', include both 'error' and 'system_critical_error'
        if (filter === 'error') {
          return row.status === 'error' || row.status === 'system_critical_error';
        }
        // When filtering by 'conflict', check if row has any conflicts
        if (filter === 'conflict') {
          return row.conflicts && row.conflicts.length > 0;
        }
        return row.status === filter;
      });
    }

    return filtered.slice(0, maxRows);
  }, [rows, filter, maxRows]);

  const getStatusIcon = (status: RowStatus) => {
    switch (status) {
      case 'valid':
        return <StatusIcon status="success" size={16} />;
      case 'warning':
        return <StatusIcon status="warning" size={16} />;
      case 'error':
      case 'system_critical_error':
        return <StatusIcon status="error" size={16} />;
      case 'conflict':
        return <RefreshCw size={16} className={styles.importPreviewStatusIconConflict} />;
      default:
        return null;
    }
  };

  const getRowClassName = (row: ImportRow): string => {
    return clsx(styles.importPreviewRow, {
      [styles.importPreviewRowError]: row.status === 'error' || row.status === 'system_critical_error',
      [styles.importPreviewRowWarning]: row.status === 'warning',
      [styles.importPreviewRowConflict]: row.status === 'conflict',
      [styles.importPreviewRowValid]: row.status === 'valid'
    });
  };

  const getEffectiveAction = (row: ImportRow): RowAction => {
    return rowOverrides[row.rowNumber] || row.action;
  };

  const handleActionChange = (rowNumber: number, action: RowAction) => {
    if (onActionChange) {
      onActionChange(rowNumber, action);
    }
  };

  // Fixed action options for all rows
  const AVAILABLE_ACTIONS: RowAction[] = ['create', 'update', 'skip'];

  // Format action label for display
  const formatActionLabel = (action: RowAction): string => {
    switch (action) {
      case 'create':
        return 'Create';
      case 'update':
        return 'Update';
      case 'skip':
        return 'Skip';
    }
  };

  // Render cell content based on column key
  const renderCell = (column: TableColumn, row: ImportRow) => {
    const effectiveAction = getEffectiveAction(row);
    const hasDetails = row.errors.length > 0 || row.warnings.length > 0 || row.conflicts.length > 0;

    switch (column.key) {
      case 'rowNumber':
        return <div className={styles.importPreviewCellRow}>{row.rowNumber}</div>;

      case 'status':
        return <div className={styles.importPreviewCellStatus}>{getStatusIcon(row.status)}</div>;

      case 'action':
        return (
          <div className={styles.importPreviewCellAction}>
            {onActionChange ? (
              <Select
                value={effectiveAction}
                onChange={(value) => handleActionChange(row.rowNumber, value as RowAction)}
                options={AVAILABLE_ACTIONS.map((action) => ({
                  value: action,
                  label: formatActionLabel(action)
                }))}
                className={clsx(styles.importPreviewActionSelect, {
                  [styles.importPreviewActionSelectCreate]: effectiveAction === 'create',
                  [styles.importPreviewActionSelectUpdate]: effectiveAction === 'update',
                  [styles.importPreviewActionSelectSkip]: effectiveAction === 'skip'
                })}
                dataTestId={`import-action-${row.rowNumber}`}
              />
            ) : (
              <span className={clsx(styles.importPreviewActionBadge, {
                [styles.importPreviewActionBadgeCreate]: effectiveAction === 'create',
                [styles.importPreviewActionBadgeUpdate]: effectiveAction === 'update',
                [styles.importPreviewActionBadgeSkip]: effectiveAction === 'skip'
              })}>
                {formatActionLabel(effectiveAction)}
              </span>
            )}
          </div>
        );

      case 'contract_number':
        return <div className={styles.importPreviewCellContract}>{row.data.contract_number || '-'}</div>;

      case 'supplier':
        return (
          <div className={styles.importPreviewCellSupplier}>
            <div className={styles.importPreviewSupplierInfo}>
              <span className={styles.importPreviewSupplierName}>{row.data.supplier_name || '-'}</span>
              {row.data.supplier_number && (
                <span className={styles.importPreviewSupplierId}>{row.data.supplier_number}</span>
              )}
            </div>
          </div>
        );

      case 'tier':
        return (
          <div className={styles.importPreviewCellTier}>
            {row.data.tier ? (
              <TierBadge variant={tierToVariant(row.data.tier)}>{row.data.tier}</TierBadge>
            ) : (
              <span>-</span>
            )}
          </div>
        );

      case 'details':
        return (
          <div className={styles.importPreviewCellDetails}>
            {hasDetails ? (
              <div className={styles.importPreviewDetailsList}>
                {row.errors.map((e) => (
                  <div key={`err-${e.field}-${e.message}`} className={clsx(styles.importPreviewDetailItem, styles.importPreviewDetailItemError)}>
                    <StatusIcon status="error" size={12} />
                    <span className={styles.importPreviewDetailField}>{e.field}:</span>
                    <span className={styles.importPreviewDetailMessage}>{e.message}</span>
                  </div>
                ))}
                {row.warnings.map((w) => (
                  <div key={`warn-${w.field}-${w.message}`} className={clsx(styles.importPreviewDetailItem, styles.importPreviewDetailItemWarning)}>
                    <StatusIcon status="warning" size={12} />
                    <span className={styles.importPreviewDetailField}>{w.field}:</span>
                    <span className={styles.importPreviewDetailMessage}>{w.message}</span>
                  </div>
                ))}
                {row.conflicts.map((c) => {
                  const isCreateContact = c.suggestedAction === 'create_contact';
                  return (
                    <div
                      key={`conf-${c.message}`}
                      className={clsx(
                        styles.importPreviewDetailItem,
                        isCreateContact ? styles.importPreviewDetailItemInfo : styles.importPreviewDetailItemConflict
                      )}
                    >
                      {isCreateContact ? <UserPlus size={12} /> : <RefreshCw size={12} />}
                      <span className={styles.importPreviewDetailMessage}>
                        {isCreateContact ? `Contact will be created for: ${extractQuotedValue(c.message)}` : c.message}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.importPreviewNoIssues}>
                <StatusIcon status="success" size={12} />
                Ready to import
              </div>
            )}
          </div>
        );

      default:
        return '-';
    }
  };

  if (filteredRows.length === 0) {
    return <div className={styles.importPreviewEmpty}>No rows match the current filter.</div>;
  }

  return (
    <div className={styles.importPreview}>
      <RZTable
        data={filteredRows}
        columns={IMPORT_PREVIEW_COLUMNS}
        getRowKey={(row) => row.rowNumber}
        renderCell={renderCell}
        rowClassName={getRowClassName}
        className={styles.importPreviewTableContainer}
      />

      {rows.length > maxRows && (
        <div className={styles.importPreviewTruncatedMessage}>
          Showing {maxRows} of {filter === 'all' ? rows.length : filteredRows.length} rows
        </div>
      )}
    </div>
  );
};

export default ImportPreviewTable;
